/**
 * 견적 계산 · 견적서 인쇄
 *
 * 패널 면적(㎡) — 이전 시스템(도면 exe) 규칙, 옛 견적 4,517건 중 91% 일치:
 *   벽 = 외곽 둘레 × 높이(HEIGHT)  +  천장 = 외곽 면적(천장 있을 때)  +  바닥 = 외곽 면적(바닥 있을 때)  +  칸막이 = 길이 × 높이
 *   (이전 시스템은 천장을 항상 넣었음 — 여기서는 천장 설치 여부를 따름)
 * 문 단가: 단가표에서 문 폭 이상인 가장 작은 폭의 두께별 단가 + 재질 할증(SUS 단면·양면)
 * 합계: 줄 금액 = 반올림(수량×단가), 공급가액 = 줄 합 − 할인, 부가세 = 공급가액×10% 원 미만 버림 (서버도 같은 식으로 다시 계산)
 */
import { Door, MiddleWall, MaterialType } from '../types';
import { getMaterialSpec } from './materialSpecs';

export type LineKind = 'PANEL' | 'DOOR' | 'ITEM' | 'ETC';
export interface QuoteLine { KIND: LineKind; ITEM_NM: string; SPEC: string | null; QTY: number; UNIT: string | null; UNIT_COST: number; BIGO: string | null }
export interface PriceTable {
  panel: Record<string, Record<string, number>>;  // 재질 → 두께 → ㎡당
  door: Record<string, Record<string, number>>;   // 문 폭 → 두께 → 개당
  doorAdd: Record<string, number>;                // 재질 → 문 할증
}
interface Pt { x: number; y: number }

export const polyPerimeter = (pts: Pt[]) => pts.reduce((a, p, i) => { const q = pts[(i + 1) % pts.length]; return a + Math.hypot(q.x - p.x, q.y - p.y); }, 0);
export const polyArea = (pts: Pt[]) => Math.abs(pts.reduce((a, p, i) => { const q = pts[(i + 1) % pts.length]; return a + p.x * q.y - q.x * p.y; }, 0)) / 2;
const r2 = (v: number) => Math.round(v * 100) / 100;

export interface PanelAreaInput { outline: Pt[]; height: number; ceiling: boolean; floor: boolean; partitions: { length: number }[] }
export function panelAreas(o: PanelAreaInput) {
  const wall = (polyPerimeter(o.outline) * o.height) / 1e6;
  const roof = polyArea(o.outline) / 1e6;
  const part = o.partitions.reduce((a, p) => a + p.length * o.height, 0) / 1e6;
  const ceiling = o.ceiling ? roof : 0, floor = o.floor ? roof : 0;
  return { wall, ceiling, floor, part, total: wall + ceiling + floor + part };
}

/** 문 폭 이상인 가장 작은 단가표 폭 (없으면 null) */
export function doorPrice(p: PriceTable, width: number, thick: number, material: string): number | null {
  const ws = Object.keys(p.door).map(Number).filter(w => w >= width && p.door[w]?.[thick] != null).sort((a, b) => a - b);
  if (!ws.length) return null;
  return p.door[ws[0]][thick] + (p.doorAdd[material] ?? 0);
}

export interface AutoInput {
  material: MaterialType; thick: number; height: number; ceiling: boolean; floor: boolean;
  outline: Pt[]; middleWalls: MiddleWall[]; doors: Door[];
}
/** 도면에서 자동으로 나오는 줄: 패널 1줄 + 문 규격별 1줄 */
export function autoLines(a: AutoInput, p: PriceTable): QuoteLine[] {
  const mat = getMaterialSpec(a.material).label;
  const ar = panelAreas({ outline: a.outline, height: a.height, ceiling: a.ceiling, floor: a.floor, partitions: a.middleWalls });
  const parts = [['벽', ar.wall], ['천장', ar.ceiling], ['바닥', ar.floor], ['칸막이', ar.part]].filter(([, v]) => (v as number) > 0)
    .map(([k, v]) => `${k} ${(v as number).toFixed(2)}`);
  const lines: QuoteLine[] = [{
    KIND: 'PANEL', ITEM_NM: '우레탄판넬', SPEC: `${mat} ${a.thick}T`, QTY: r2(ar.total), UNIT: '㎡',
    UNIT_COST: p.panel[a.material]?.[a.thick] ?? 0, BIGO: parts.join(' · '),
  }];
  const groups = new Map<string, { w: number; h: number; n: number }>();
  a.doors.forEach(d => { const k = `${d.width}×${d.height}`; const g = groups.get(k) ?? { w: d.width, h: d.height, n: 0 }; g.n++; groups.set(k, g); });
  Array.from(groups.values()).sort((x, y) => x.w - y.w).forEach(g => {
    const c = doorPrice(p, g.w, a.thick, a.material);
    lines.push({ KIND: 'DOOR', ITEM_NM: '판넬도어', SPEC: `${g.w}×${g.h}`, QTY: g.n, UNIT: '개', UNIT_COST: c ?? 0,
      BIGO: c == null ? '단가표에 없는 폭 — 단가 입력' : (p.doorAdd[a.material] ? `${mat} 할증 포함` : null) });
  });
  return lines;
}

export function quoteTotals(lines: QuoteLine[], dc: number) {
  const amounts = lines.map(l => Math.round(l.QTY * l.UNIT_COST));
  const sum = amounts.reduce((a, v) => a + v, 0);
  const supply = sum - dc;
  const vat = Math.floor(supply * 0.1);
  return { amounts, sum, supply, vat, total: supply + vat };
}

/** 숫자 → 한글 금액 (일금 … 원정) */
export function hangulAmount(n: number): string {
  if (!n) return '영';
  const d = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'], s = ['', '십', '백', '천'], big = ['', '만', '억', '조'];
  let out = '', i = 0;
  for (let v = Math.floor(Math.abs(n)); v > 0; v = Math.floor(v / 10000), i++) {
    const chunk = v % 10000;
    if (!chunk) continue;
    let c = '';
    String(chunk).padStart(4, '0').split('').forEach((ch, j) => { const k = +ch; if (k) c += d[k] + s[3 - j]; });
    out = c + big[i] + out;
  }
  return (n < 0 ? '마이너스 ' : '') + out;
}

// ── 인쇄 ──
const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const won = (n: number) => Math.round(n).toLocaleString('ko-KR');
const bizFmt = (s?: string | null) => (s && s.length === 10 ? `${s.slice(0, 3)}-${s.slice(3, 5)}-${s.slice(5)}` : s || '');
const qtyFmt = (q: number) => (Number.isInteger(q) ? String(q) : q.toFixed(2));

export interface QuoteParty { name?: string | null; bizNo?: string | null; ceo?: string | null; tel?: string | null; fax?: string | null; addr?: string | null; mgr?: string | null; email?: string | null }
export interface PrintQuoteOptions {
  estiNo: string; date: string; siteName: string;
  supplier: QuoteParty; customer: QuoteParty | null;
  spec?: { material: string; thick: number; horizontal: number; vertical: number; height: number; ceiling: boolean; floor: boolean; partitions: number }; // 수기 견적은 없음
  lines: QuoteLine[]; dc: number; memo: string; terms: string; staff?: string;
  account?: string; // 입금 계좌 (은행 계좌번호 예금주)
}
/** win: 저장 등 기다리는 작업이 있으면 클릭 때 미리 연 창 (팝업 차단 피함) */
export function printQuote(o: PrintQuoteOptions, win?: Window | null) {
  const t = quoteTotals(o.lines, o.dc);
  const rows = o.lines.map((l, i) => `<tr><td class="c">${i + 1}</td><td>${esc(l.ITEM_NM)}</td><td>${esc(l.SPEC || '')}</td>
    <td class="n">${qtyFmt(l.QTY)}</td><td class="c">${esc(l.UNIT || '')}</td><td class="n">${won(l.UNIT_COST)}</td><td class="n">${won(t.amounts[i])}</td><td class="bigo">${esc(l.BIGO || '')}</td></tr>`).join('');
  const blank = Array.from({ length: Math.max(0, 8 - o.lines.length) }, () => '<tr><td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>').join('');
  const party = (p: QuoteParty | null, title: string) => `<table class="party"><tr><th rowspan="5" class="vt">${title}</th>
    <th>상호</th><td colspan="3"><b>${esc(p?.name || '')}</b></td></tr>
    <tr><th>사업자번호</th><td>${esc(bizFmt(p?.bizNo))}</td><th>대표</th><td>${esc(p?.ceo || '')}</td></tr>
    <tr><th>주소</th><td colspan="3">${esc(p?.addr || '')}</td></tr>
    <tr><th>전화</th><td>${esc(p?.tel || '')}</td><th>팩스</th><td>${esc(p?.fax || '')}</td></tr>
    <tr><th>담당</th><td colspan="3">${esc([p?.mgr, p?.email].filter(Boolean).join(' · '))}</td></tr></table>`;
  const s = o.spec;
  const specTxt = !s ? '' : `${s.material} ${s.thick}T · 가로 ${s.horizontal} × 세로 ${s.vertical} × 높이 ${s.height} · 천장 ${s.ceiling ? '있음' : '없음'} · 바닥 ${s.floor ? '있음' : '없음'}${s.partitions ? ` · 칸막이 ${s.partitions}` : ''}`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>견적서 - ${esc(o.estiNo)} ${esc(o.siteName)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: "Malgun Gothic", Arial, sans-serif; margin: 0; color: #111; font-size: 12px; }
  h1 { text-align: center; font-size: 28px; letter-spacing: 18px; margin: 0 0 4px; }
  .meta { display: flex; justify-content: space-between; margin: 10px 0 6px; font-size: 12px; }
  .parties { display: flex; gap: 8px; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #555; padding: 4px 6px; }
  th { background: #f1f3f5; font-weight: normal; white-space: nowrap; }
  .party { flex: 1; font-size: 11px; }
  .vt { width: 22px; writing-mode: vertical-rl; letter-spacing: 6px; font-weight: bold; }
  .amount { margin: 10px 0; border: 2px solid #111; padding: 8px 12px; font-size: 15px; display: flex; justify-content: space-between; }
  .items th { background: #e7eef8; }
  .n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .c { text-align: center; }
  .bigo { font-size: 10px; color: #444; }
  .sum th { text-align: right; padding-right: 10px; }
  .box { border: 1px solid #555; padding: 8px 10px; margin-top: 8px; white-space: pre-wrap; line-height: 1.6; font-size: 11px; }
  .sign { margin-top: 14px; text-align: right; font-size: 12px; }
</style></head><body>
<h1>견적서</h1>
<div class="meta"><span>견적번호 <b>${esc(o.estiNo)}</b> · 견적일 ${esc(o.date)}</span><span>현장 <b>${esc(o.siteName || '-')}</b></span></div>
<div class="parties">${party(o.customer, '공급받는자')}${party(o.supplier, '공급자')}</div>
<div class="amount"><span>합계금액 (공급가액 + 부가세)</span><span><b>일금 ${hangulAmount(t.total)}원정</b> (₩${won(t.total)})</span></div>
${specTxt ? `<div style="margin-bottom:6px">사양: ${esc(specTxt)}</div>` : ''}
<table class="items">
  <tr><th style="width:28px">No</th><th>품명</th><th>규격</th><th>수량</th><th>단위</th><th>단가</th><th>금액</th><th>비고</th></tr>
  ${rows}${blank}
</table>
<table class="sum" style="margin-top:-1px">
  <tr><th>합계</th><td class="n" style="width:130px">${won(t.sum)}</td></tr>
  ${o.dc ? `<tr><th>할인</th><td class="n">-${won(o.dc)}</td></tr>` : ''}
  <tr><th>공급가액</th><td class="n">${won(t.supply)}</td></tr>
  <tr><th>부가세 (10%)</th><td class="n">${won(t.vat)}</td></tr>
  <tr><th><b>총 합계</b></th><td class="n"><b>${won(t.total)}</b></td></tr>
</table>
${o.memo ? `<div class="box"><b>비고</b>\n${esc(o.memo)}</div>` : ''}
${o.account ? `<div class="box"><b>입금 계좌</b> &nbsp; ${esc(o.account)}</div>` : ''}
${o.terms ? `<div class="box"><b>특약사항</b>\n${esc(o.terms)}</div>` : ''}
<div class="sign">${o.staff ? `담당: ${esc(o.staff)} &nbsp; ` : ''}${esc(o.supplier.name || '')} (인)</div>
<script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
  const w = win ?? window.open('', '_blank');
  if (!w) { alert('팝업이 차단되었습니다. 브라우저에서 팝업을 허용해 주세요.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
